import type { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  formatPhoneLocal,
  passengerBoardedComputed,
  useFormats,
  type IRideInvestigationPassengerDto,
} from "@ryde/shared";

import {
  PASSENGER_STATUS_BG,
  PASSENGER_STATUS_COLOR,
} from "../investigation.constants";
import {
  PassengerRowStyled,
  PassengerStatusChipStyled,
} from "../investigation.panel.styled";

interface IInvestigationPassengerRowProps {
  passenger: IRideInvestigationPassengerDto;
  /** True while playback sits at the station this passenger boards at. */
  isActive: boolean;
  onSeek: () => void;
}

/** One roster row: who the passenger is, where they got on and off, and what the driver recorded. */
export const InvestigationPassengerRow: FC<IInvestigationPassengerRowProps> = ({
  passenger,
  isActive,
  onSeek,
}) => {
  const { t } = useTranslation();
  const { formatTimeNormalized } = useFormats();
  const label = (key: string): string =>
    t(`rideMap.investigation.passengers.${key}`);
  const status = passengerBoardedComputed(passenger);
  const pickupTime =
    passenger.pickupActualArrival ?? passenger.pickupScheduledArrival;
  const dropoffTime =
    passenger.dropoffActualArrival ?? passenger.dropoffScheduledArrival;

  return (
    <PassengerRowStyled type="button" $active={isActive} onClick={onSeek}>
      <span className="pax-seq">
        {passenger.pickupSequence !== undefined
          ? passenger.pickupSequence + 1
          : "?"}
      </span>
      <span className="pax-body">
        <span className="pax-head">
          <span className="pax-name">
            {passenger.name || `#${passenger.id}`}
          </span>
          <PassengerStatusChipStyled
            $color={PASSENGER_STATUS_COLOR[status]}
            $bg={PASSENGER_STATUS_BG[status]}
          >
            {label(`status.${status}`)}
          </PassengerStatusChipStyled>
        </span>

        <span className="pax-legs">
          <span className={passenger.pickupStationName ? undefined : "muted"}>
            {label("pickup")}:{" "}
            {passenger.pickupStationName || label("noStation")}
            {pickupTime ? ` · ${formatTimeNormalized(pickupTime)}` : ""}
          </span>
          <span className={passenger.dropoffStationName ? undefined : "muted"}>
            {label("dropoff")}:{" "}
            {passenger.dropoffStationName || label("noStation")}
            {dropoffTime ? ` · ${formatTimeNormalized(dropoffTime)}` : ""}
          </span>
        </span>

        <span className="pax-meta">
          {passenger.unitName && (
            <span>
              {label("unit")}: {passenger.unitName}
            </span>
          )}
          {passenger.seats > 1 && (
            <span>
              {label("seats")}: {passenger.seats}
            </span>
          )}
          {passenger.phone && <span>{formatPhoneLocal(passenger.phone)}</span>}
          {passenger.boardedAt && (
            <span>
              {label("boardedAt")}: {formatTimeNormalized(passenger.boardedAt)}
            </span>
          )}
          {passenger.hasDog && <span>{label("dog")}</span>}
          {passenger.catchARide && <span>{label("catchARide")}</span>}
        </span>

        {passenger.boardedNote && (
          <span className="pax-note">
            {label("note")}: {passenger.boardedNote}
          </span>
        )}
      </span>
    </PassengerRowStyled>
  );
};
